import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { api, naira, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";
import { Loader2, Plus, Users, Wallet, TrendingUp, MapPin, Send, PackageCheck, Upload, FileCheck2, CheckCircle2, XCircle, Download, Printer, Store, ReceiptText, Percent, Share2 } from "lucide-react";
import { PaymentDialog } from "@/components/PaymentDialog";
import { FundingHistory } from "@/components/FundingHistory";
import { toast } from "sonner";

function Metric({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-2xl border border-emerald-100 bg-white p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500 font-medium">{label}</span>
        <Icon className={`w-4 h-4 ${accent || "text-emerald-500"}`} />
      </div>
      <div className="mt-2 font-bold text-2xl text-emerald-950 tabular-nums">{value}</div>
    </div>
  );
}

export default function SponsorDashboard() {
  const { user } = useAuth();
  const [dash, setDash] = useState(null);
  const [bens, setBens] = useState([]);
  const [stxns, setStxns] = useState([]);
  const [creating, setCreating] = useState(false);
  const [progOpen, setProgOpen] = useState(false);
  const [allocOpen, setAllocOpen] = useState(null);
  const [phones, setPhones] = useState("");
  const [bulkOpen, setBulkOpen] = useState(null);
  const [bulkReview, setBulkReview] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [prog, setProg] = useState({ name: "", code: "", beneficiary_target: 0, benefit_per_beneficiary: 0, benefit_type: "food", start_date: "", end_date: "" });

  const load = () => {
    api.get("/sponsors/dashboard").then((r) => setDash(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/sponsors/beneficiaries").then((r) => setBens(r.data.beneficiaries)).catch(() => {});
    api.get("/sponsors/transactions").then((r) => setStxns(r.data.transactions)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const PROG_STATUS = {
    draft: { label: "Draft", cls: "bg-gray-100 text-gray-600" },
    pending_funding: { label: "Pending Funding", cls: "bg-amber-100 text-amber-800" },
    funding_confirmed: { label: "Funding Confirmed", cls: "bg-blue-100 text-blue-800" },
    active: { label: "Active", cls: "bg-emerald-100 text-emerald-800" },
    suspended: { label: "Suspended", cls: "bg-red-100 text-red-800" },
    completed: { label: "Completed", cls: "bg-gray-200 text-gray-700" },
  };

  const downloadBlob = async (url, filename) => {
    try {
      const res = await api.get(url, { responseType: "blob" });
      const href = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = href; a.download = filename; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(href);
      toast.success("Download started");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const createProg = async () => {
    setCreating(true);
    try {
      await api.post("/programmes", { ...prog, eligible_states: [] });
      toast.success("Programme created");
      setProg({ name: "", code: "", beneficiary_target: 0, benefit_per_beneficiary: 0, benefit_type: "food", start_date: "", end_date: "" });
      setProgOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCreating(false); }
  };

  const allocate = async () => {
    const list = phones.split(/[\s,\n]+/).map((p) => p.trim()).filter(Boolean);
    if (!list.length) return toast.error("Enter at least one phone number");
    try {
      const { data } = await api.post(`/programmes/${allocOpen.id}/allocate`, { phones: list });
      toast.success(`${data.issued} voucher(s) issued. ${data.skipped.length} not registered. ${data.already_allocated.length} already had one.`);
      setAllocOpen(null); setPhones(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const parseCsv = (text) => {
    const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (!lines.length) return [];
    let header = null;
    const first = lines[0].toLowerCase();
    if (first.includes("phone")) { header = lines[0].split(",").map((h) => h.trim().toLowerCase()); lines.shift(); }
    const idx = (name, def) => (header ? header.indexOf(name) : def);
    return lines.map((l) => {
      const c = l.split(",").map((x) => x.trim());
      return {
        phone: c[idx("phone", 0)] || "",
        full_name: c[idx("full_name", 1)] || c[idx("name", 1)] || "",
        state: c[idx("state", 2)] || "",
        lga: c[idx("lga", 3)] || "",
      };
    }).filter((r) => r.phone);
  };

  const onCsvFile = async (e, prog) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const rows = parseCsv(text);
    if (!rows.length) return toast.error("No valid rows found. Expected columns: phone, full_name, state, lga");
    setBulkBusy(true);
    try {
      const { data } = await api.post(`/programmes/${prog.id}/bulk-review`, { rows });
      setBulkReview(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBulkBusy(false); e.target.value = ""; }
  };

  const approveBulk = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post(`/bulk-batches/${bulkReview.batch_id}/approve`);
      toast.success(`${data.created_beneficiaries} new beneficiaries, ${data.issued_vouchers} vouchers issued.`);
      setBulkReview(null); setBulkOpen(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBulkBusy(false); }
  };

  const downloadReport = async () => {
    try {
      const res = await api.get("/sponsors/impact-report", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "tmf-impact-report.csv"; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
      toast.success("Impact report downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!dash) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>;
  const m = dash.metrics;
  const chartData = dash.programmes.map((p) => ({ name: p.code, Allocated: p.total_value }));

  return (
    <div>
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-10">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <span className="text-xs font-mono uppercase tracking-widest text-emerald-700">Sponsor Dashboard</span>
            <h1 className="mt-2 font-display font-bold text-3xl text-emerald-950">{user?.name}</h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <PaymentDialog sponsorEmail={user?.email} />
            <Button variant="outline" data-testid="share-card-btn" onClick={() => window.location.assign("/sponsor/impact-card")} className="rounded-full h-11 px-4 border-emerald-200 text-emerald-800"><Share2 className="w-4 h-4 mr-2" /> Share Card</Button>
            <Button variant="outline" data-testid="download-report-csv-btn" onClick={downloadReport} className="rounded-full h-11 px-4 border-emerald-200 text-emerald-800"><Download className="w-4 h-4 mr-2" /> Report (CSV)</Button>
            <Button variant="outline" data-testid="print-report-btn" onClick={() => window.print()} className="rounded-full h-11 px-4 border-emerald-200 text-emerald-800"><Printer className="w-4 h-4 mr-2" /> PDF</Button>
          <Dialog open={progOpen} onOpenChange={setProgOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-programme-btn" className="bg-emerald-600 hover:bg-emerald-700 rounded-full h-11 px-5"><Plus className="w-4 h-4 mr-2" /> Create Programme</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Create a Programme</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label>Programme Name</Label><Input data-testid="prog-name" value={prog.name} onChange={(e) => setProg({ ...prog, name: e.target.value })} placeholder="e.g. City Food Access Programme" className="mt-1.5 h-11" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Programme Code</Label><Input data-testid="prog-code" value={prog.code} onChange={(e) => setProg({ ...prog, code: e.target.value.toUpperCase() })} placeholder="e.g. CITY" className="mt-1.5 h-11 font-mono" /></div>
                  <div><Label>Beneficiary Target</Label><Input data-testid="prog-target" type="number" min="0" value={prog.beneficiary_target || ""} placeholder="0" onFocus={(e) => e.target.select()} onChange={(e) => setProg({ ...prog, beneficiary_target: +e.target.value })} className="mt-1.5 h-11" /></div>
                </div>
                <div>
                  <div><Label>Benefit / Beneficiary (₦)</Label><Input data-testid="prog-value" type="number" min="0" value={prog.benefit_per_beneficiary || ""} placeholder="0" onFocus={(e) => e.target.select()} onChange={(e) => setProg({ ...prog, benefit_per_beneficiary: +e.target.value })} className="mt-1.5 h-11" /></div>
                </div>
                <div>
                  <Label>Benefit Type</Label>
                  <Select value={prog.benefit_type} onValueChange={(v) => setProg({ ...prog, benefit_type: v })}>
                    <SelectTrigger data-testid="prog-benefit-type" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="food">Food Only</SelectItem>
                      <SelectItem value="cashback">Cashback</SelectItem>
                      <SelectItem value="split">Food + Cashback (50/50)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Start Date</Label><Input data-testid="prog-start" type="date" value={prog.start_date} onChange={(e) => setProg({ ...prog, start_date: e.target.value })} className="mt-1.5 h-11" /></div>
                  <div><Label>End Date</Label><Input data-testid="prog-end" type="date" value={prog.end_date} onChange={(e) => setProg({ ...prog, end_date: e.target.value })} className="mt-1.5 h-11" /></div>
                </div>
                <div className="text-sm text-gray-500">Total Programme Value: <b className="text-emerald-700">{naira(prog.beneficiary_target * prog.benefit_per_beneficiary)}</b></div>
              </div>
              <DialogFooter>
                <Button data-testid="prog-submit" onClick={createProg} disabled={creating || !prog.name || !prog.code} className="bg-emerald-600 hover:bg-emerald-700">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Programme"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <div className="mt-8 grid gap-4 grid-cols-2 lg:grid-cols-4">
          <Metric icon={Wallet} label="Total Allocation" value={naira(m.total_allocation)} />
          <Metric icon={Users} label="Beneficiaries" value={m.total_beneficiaries.toLocaleString()} />
          <Metric icon={TrendingUp} label="Redeemed" value={naira(m.redeemed)} accent="text-red-500" />
          <Metric icon={PackageCheck} label="Remaining" value={naira(m.remaining)} />
        </div>
        <div className="mt-4 grid gap-4 grid-cols-2 lg:grid-cols-4">
          <Metric icon={PackageCheck} label="Programmes" value={m.total_programmes} />
          <Metric icon={Wallet} label="Activated Value" value={naira(m.activated_value)} />
          <Metric icon={PackageCheck} label="Active Vouchers" value={m.active_vouchers} />
          <Metric icon={MapPin} label="States Covered" value={m.states_covered} />
        </div>
        <div className="mt-4 grid gap-4 grid-cols-2 lg:grid-cols-4">
          <Metric icon={Percent} label="Redemption Rate" value={`${m.redemption_rate}%`} />
          <Metric icon={Store} label="Vendors Participating" value={m.vendors_participating} />
          <Metric icon={ReceiptText} label="Total Transactions" value={m.total_transactions.toLocaleString()} />
          <Metric icon={TrendingUp} label="Economic Activity" value={naira(m.total_economic_activity)} />
        </div>

        {dash.programmes.length > 0 && (
          <div className="mt-8 rounded-2xl border border-emerald-100 bg-white p-6">
            <h3 className="font-display font-semibold text-lg text-emerald-950 mb-4">Allocation by Programme</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData}>
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000000 ? `₦${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `₦${(v / 1000).toFixed(0)}k` : `₦${v}`} />
                <Tooltip formatter={(v) => naira(v)} />
                <Bar dataKey="Allocated" radius={[8, 8, 0, 0]}>
                  {chartData.map((entry) => <Cell key={entry.name} fill="#059669" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        <h2 className="mt-10 font-display font-bold text-xl text-emerald-950">My Programmes</h2>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          {dash.programmes.length === 0 && <p className="text-gray-500 text-sm">No programmes yet. Create one to start allocating benefits.</p>}
          {dash.programmes.map((p) => (
            <div key={p.id} data-testid={`programme-card-${p.code}`} className="rounded-2xl border border-emerald-100 bg-white p-6">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-mono text-xs text-emerald-700">{p.code}</div>
                  <h3 className="font-display font-semibold text-lg text-emerald-950">{p.name}</h3>
                </div>
                <Badge className="bg-emerald-100 text-emerald-800 uppercase">{p.benefit_type}</Badge>
              </div>
              <div className="mt-2">
                <Badge className={(PROG_STATUS[p.status] || PROG_STATUS.pending_funding).cls}>{(PROG_STATUS[p.status] || PROG_STATUS.pending_funding).label}</Badge>
                {p.status !== "active" && p.status !== "completed" && (
                  <p className="mt-1.5 text-[11px] text-amber-700">Benefits are held inactive until a TMF Administrator confirms funding and activates this programme.</p>
                )}
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
                <div><div className="text-gray-400 text-xs">Per Ben.</div><div className="font-semibold">{naira(p.benefit_per_beneficiary)}</div></div>
                <div><div className="text-gray-400 text-xs">Target</div><div className="font-semibold">{p.beneficiary_target.toLocaleString()}</div></div>
                <div><div className="text-gray-400 text-xs">Total</div><div className="font-semibold">{naira(p.total_value)}</div></div>
              </div>
              <div className="mt-3 grid grid-cols-4 gap-2 text-center rounded-xl bg-emerald-50/70 border border-emerald-100 p-3">
                <div><div className="text-[10px] uppercase tracking-wide text-gray-400">Redeemed</div><div className="font-semibold text-sm text-emerald-800">{p.redemption_rate}%</div></div>
                <div><div className="text-[10px] uppercase tracking-wide text-gray-400">Bens</div><div className="font-semibold text-sm">{(p.beneficiaries || 0).toLocaleString()}</div></div>
                <div><div className="text-[10px] uppercase tracking-wide text-gray-400">Vendors</div><div className="font-semibold text-sm">{p.vendors || 0}</div></div>
                <div><div className="text-[10px] uppercase tracking-wide text-gray-400">Txns</div><div className="font-semibold text-sm">{p.transactions || 0}</div></div>
              </div>
              <div className="mt-4 flex gap-2">
                <Button data-testid={`allocate-btn-${p.code}`} onClick={() => setAllocOpen(p)} variant="outline" className="flex-1 border-emerald-200 text-emerald-800"><Send className="w-4 h-4 mr-2" /> Allocate</Button>
                <Button data-testid={`bulk-btn-${p.code}`} onClick={() => { setBulkReview(null); setBulkOpen(p); }} className="flex-1 bg-emerald-600 hover:bg-emerald-700"><Upload className="w-4 h-4 mr-2" /> Bulk CSV</Button>
              </div>
            </div>
          ))}
        </div>

        {/* Beneficiaries (own programmes only) */}
        <div className="mt-10 flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-display font-bold text-xl text-emerald-950">My Beneficiaries <span className="text-sm text-gray-400">({bens.length})</span></h2>
          <div className="flex gap-2">
            <Button data-testid="dl-beneficiaries-xlsx" size="sm" variant="outline" onClick={() => downloadBlob("/sponsors/beneficiaries/export?format=xlsx", "tmf-my-beneficiaries.xlsx")} className="border-emerald-200 text-emerald-800"><Download className="w-4 h-4 mr-1" /> Excel</Button>
            <Button data-testid="dl-beneficiaries-csv" size="sm" variant="outline" onClick={() => downloadBlob("/sponsors/beneficiaries/export?format=csv", "tmf-my-beneficiaries.csv")} className="border-emerald-200 text-emerald-800"><Download className="w-4 h-4 mr-1" /> CSV</Button>
          </div>
        </div>
        <div className="mt-4 rounded-2xl border border-emerald-100 bg-white overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-emerald-50/60">
              <TableHead>TMF ID</TableHead><TableHead>Name</TableHead><TableHead>Voucher ID</TableHead><TableHead>Programme</TableHead>
              <TableHead>Location</TableHead><TableHead className="text-right">Benefit</TableHead><TableHead className="text-right">Redeemed</TableHead>
              <TableHead className="text-right">Balance</TableHead><TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {bens.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-gray-500 py-10">No beneficiaries allocated yet.</TableCell></TableRow>}
              {bens.slice(0, 100).map((v) => (
                <TableRow key={v.id} data-testid={`sponsor-ben-${v.code}`}>
                  <TableCell className="font-mono text-xs">{v.tmf_id}</TableCell>
                  <TableCell className="text-sm">{v.beneficiary_name || "—"}</TableCell>
                  <TableCell className="font-mono text-[11px] font-semibold text-emerald-800">{v.tmf_voucher_id || v.code}</TableCell>
                  <TableCell className="text-sm">{v.programme_name}</TableCell>
                  <TableCell className="text-xs">{[v.lga, v.state].filter(Boolean).join(", ") || "—"}</TableCell>
                  <TableCell className="text-right">{naira(v.original_value)}</TableCell>
                  <TableCell className="text-right text-red-600">{naira(v.used)}</TableCell>
                  <TableCell className="text-right">{naira(v.balance)}</TableCell>
                  <TableCell><Badge className={v.status === "active" ? "bg-emerald-100 text-emerald-800" : v.status === "pending" ? "bg-amber-100 text-amber-800" : "bg-gray-200 text-gray-700"}>{v.status}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Transaction monitoring */}
        <div className="mt-10 flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-display font-bold text-xl text-emerald-950">Transaction Monitoring <span className="text-sm text-gray-400">({stxns.length})</span></h2>
          <div className="flex gap-2">
            <Button data-testid="dl-transactions-xlsx" size="sm" variant="outline" onClick={() => downloadBlob("/sponsors/transactions/export?format=xlsx", "tmf-my-transactions.xlsx")} className="border-emerald-200 text-emerald-800"><Download className="w-4 h-4 mr-1" /> Excel</Button>
            <Button data-testid="dl-transactions-csv" size="sm" variant="outline" onClick={() => downloadBlob("/sponsors/transactions/export?format=csv", "tmf-my-transactions.csv")} className="border-emerald-200 text-emerald-800"><Download className="w-4 h-4 mr-1" /> CSV</Button>
          </div>
        </div>
        <div className="mt-4 rounded-2xl border border-emerald-100 bg-white overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-emerald-50/60">
              <TableHead>Txn Ref</TableHead><TableHead>Type</TableHead><TableHead>Date</TableHead><TableHead>TMF ID</TableHead>
              <TableHead>Vendor</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {stxns.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-gray-500 py-10">No redemptions yet.</TableCell></TableRow>}
              {stxns.slice(0, 100).map((t) => (
                <TableRow key={t.id} data-testid={`sponsor-txn-${t.txn_id}`}>
                  <TableCell className="font-mono text-xs">{t.txn_id}</TableCell>
                  <TableCell className="capitalize text-sm">{t.type}</TableCell>
                  <TableCell className="text-xs">{(t.timestamp || "").slice(0, 10)}</TableCell>
                  <TableCell className="font-mono text-xs">{t.tmf_id}</TableCell>
                  <TableCell className="text-xs">{t.vendor_name}</TableCell>
                  <TableCell className={`text-right font-semibold ${t.type === "reversal" ? "text-blue-600" : "text-emerald-700"}`}>{naira(t.amount)}</TableCell>
                  <TableCell><Badge className={t.status === "approved" ? "bg-emerald-100 text-emerald-800" : t.status === "reversed" ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-700"}>{t.status}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <Dialog open={!!bulkOpen} onOpenChange={(o) => { if (!o) { setBulkOpen(null); setBulkReview(null); } }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Bulk Enrollment — {bulkOpen?.name}</DialogTitle></DialogHeader>
            {!bulkReview ? (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">Upload a CSV with columns <span className="font-mono text-emerald-700">phone, full_name, state, lga</span>. The list is validated & de-duplicated for your review before any voucher is issued.</p>
                <label className="flex flex-col items-center justify-center border-2 border-dashed border-emerald-200 rounded-2xl p-8 cursor-pointer hover:bg-emerald-50 transition-colors">
                  <Upload className="w-8 h-8 text-emerald-500" />
                  <span className="mt-2 text-sm font-medium text-emerald-800">Choose CSV file</span>
                  <input data-testid="bulk-csv-input" type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => onCsvFile(e, bulkOpen)} />
                </label>
                {bulkBusy && <div className="flex items-center gap-2 text-sm text-emerald-700"><Loader2 className="w-4 h-4 animate-spin" /> Validating…</div>}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-4 gap-2 text-center">
                  {[["new","New"],["existing","Existing"],["already_allocated","Skip"],["invalid","Invalid"]].map(([k, l]) => (
                    <div key={k} className="rounded-xl bg-emerald-50 border border-emerald-100 p-3">
                      <div className="font-display font-bold text-xl text-emerald-950 tabular-nums">{bulkReview.summary[k]}</div>
                      <div className="text-[11px] text-gray-500">{l}</div>
                    </div>
                  ))}
                </div>
                <div className="max-h-64 overflow-auto rounded-xl border border-emerald-100">
                  <Table>
                    <TableHeader><TableRow className="bg-emerald-50/60"><TableHead>Phone</TableHead><TableHead>Name</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {bulkReview.rows.map((r, i) => (
                        <TableRow key={`${r.phone_raw}-${i}`} data-testid={`bulk-row-${i}`}>
                          <TableCell className="font-mono text-xs">{r.phone_raw}</TableCell>
                          <TableCell className="text-sm">{r.full_name || "—"}</TableCell>
                          <TableCell>
                            <Badge className={r.action === "invalid" || r.action === "already_allocated" ? "bg-gray-200 text-gray-700" : "bg-emerald-100 text-emerald-800"}>
                              {r.action === "new" && <CheckCircle2 className="w-3 h-3 mr-1" />}
                              {r.action === "invalid" && <XCircle className="w-3 h-3 mr-1" />}
                              {r.action.replace("_", " ")}
                            </Badge>
                            {r.note && <div className="text-[11px] text-gray-500 mt-1">{r.note}</div>}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setBulkReview(null)}>Upload different file</Button>
                  <Button data-testid="bulk-approve-btn" onClick={approveBulk} disabled={bulkBusy || (bulkReview.summary.new + bulkReview.summary.existing === 0)} className="bg-emerald-600 hover:bg-emerald-700">
                    {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileCheck2 className="w-4 h-4 mr-2" /> Approve & Issue {bulkReview.summary.new + bulkReview.summary.existing}</>}
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={!!allocOpen} onOpenChange={(o) => !o && setAllocOpen(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Allocate — {allocOpen?.name}</DialogTitle></DialogHeader>
            <p className="text-sm text-gray-600">Enter registered beneficiary phone numbers (comma or newline separated). Vouchers are only issued to matched, registered beneficiaries.</p>
            <Textarea data-testid="allocate-phones" value={phones} onChange={(e) => setPhones(e.target.value)} placeholder="08030000001, 08030000002" rows={5} className="mt-2" />
            <DialogFooter>
              <Button data-testid="allocate-submit" onClick={allocate} className="bg-emerald-600 hover:bg-emerald-700">Issue Vouchers</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <FundingHistory />
      </div>
    </div>
  );
}
